import { Banner, Button, Heading, Paragraph, Popup, TriangleExclamationIcon } from '@quickmd-llc/design-system'

type Props = {
  isOpen: boolean
  firstName: string
  onConfirm: () => void
}

/**
 * Blocking notice shown before the provider contacts a patient who is on the in-person-visit list
 * (backend QMD-4716). `lockOpen` is what makes it blocking: it turns backdrop click and Esc into
 * no-ops and suppresses the close icon, so "Confirm and continue" is the only way out. Focus is
 * trapped by the underlying Headless UI dialog.
 */
export const InPersonRequiredNoticeModal = ({ isOpen, firstName, onConfirm }: Props) => (
  <Popup
    isOpen={isOpen}
    lockOpen
    // Deliberately inert: `lockOpen` means the only close path is the footer button. Wiring this
    // to onConfirm would silently record an acknowledgement if the modal were ever made dismissible.
    onClose={() => {}}
    styles={{ containerClassName: 'w-[512px]' }}
    header={{
      content: <Heading level='h3'>In-person visit required{firstName ? ` for ${firstName}` : ''}</Heading>,
    }}
    body={
      <div className='flex flex-col gap-sp-2'>
        <Banner
          variant='warning'
          icon={<TriangleExclamationIcon iconSize='xl' />}
          description='Patient must be informed before completing this visit.'
        />
        <Paragraph className='text-text-medium'>
          This patient is required to be seen in person to continue care. The in-person visit must be completed through
          QuickMD. Their account will be on hold until it is completed.
        </Paragraph>
      </div>
    }
    footer={
      <div className='flex justify-end'>
        <Button priority='primary' onClick={onConfirm}>
          Confirm and continue
        </Button>
      </div>
    }
  />
)
